import { Injectable } from '@angular/core';
import { BaseApiService } from '@ep/shared';
import { catchError, map, Observable, of } from 'rxjs';
import { RepresentativeAccessTypes, RepresentativeTypes } from '@shared/enums';

// Raw responses come back camelCase and, per EP.Service's ReferenceHandler.Preserve
// JSON config, wrapped with $id (objects) / $id+$values (arrays). Callers map these
// into app models themselves (see representatives.api.service.ts's mapToRepresentative),
// mirroring how MerchantService/MerchantsApiService already handle this envelope.

export interface CreateRepresentativeRequest {
  CoalitionId: number;
  NameFirst: string;
  NameLast: string;
  EmailAddress: string;
  PhoneNumber?: string;
  AccessType?: RepresentativeAccessTypes;
  RepresentativeType?: RepresentativeTypes;
  LanguageType?: number;
}

export interface UpdateRepresentativeRequest {
  NameFirst: string;
  NameLast: string;
  PhoneNumber?: string;
  AccessType?: RepresentativeAccessTypes;
  RepresentativeType?: RepresentativeTypes;
  LanguageType?: number;
}

@Injectable({
  providedIn: 'root',
})
export class RepresentativeService extends BaseApiService {
  private readonly representativeEndpoint = `/v2/Representatives`;

  getByCoalitionId(coalitionId: number): Observable<any> {
    return this.get(`${this.representativeEndpoint}/coalition/${coalitionId}`).pipe(
      catchError((err) => {
        console.error(err);
        return of(null);
      })
    );
  }

  getById(representativeId: number): Observable<any> {
    return this.get(this.representativeEndpoint, { representativeId }).pipe(
      catchError((err) => {
        console.error(err);
        return of(null);
      })
    );
  }

  create(createRepresentativeRequest: CreateRepresentativeRequest): Observable<any> {
    return this.post(`${this.representativeEndpoint}`, createRepresentativeRequest).pipe(
      catchError((err) => {
        console.error(err);
        return of(null);
      })
    );
  }

  update(representativeId: number, updateRepresentativeRequest: UpdateRepresentativeRequest): Observable<any> {
    return this.patch(`${this.representativeEndpoint}/${representativeId}`, updateRepresentativeRequest).pipe(
      catchError((err) => {
        console.error(err);
        return of(null);
      })
    );
  }

  getMerchantAccess(representativeId: number): Observable<any> {
    return this.get(`${this.representativeEndpoint}/${representativeId}/merchant-access`).pipe(
      catchError((err) => {
        console.error(err);
        return of(null);
      })
    );
  }

  saveMerchantAccess(representativeId: number, merchantIds: number[]): Observable<boolean> {
    // The backend responds 200 with an empty body (Ok()), which HttpClient parses as `null` -
    // success can't be judged by body content here, only by whether the request completed
    // without error.
    return this.put(`${this.representativeEndpoint}/${representativeId}/merchant-access`, {
      MerchantIds: merchantIds,
    }).pipe(
      map(() => true),
      catchError((err) => {
        console.error(err);
        return of(false);
      })
    );
  }
}
